import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { clientMessagesService as api } from '../../../_services/clientMessages.service';
import { conciergeService } from '../../../_services/concierge.service';
import { unwrap, useConversation, useInbox } from '../../receptionist/inboxData';
import ReceptionThread from '../../receptionist/ReceptionThread';
import SlidePanel from '../../shared/SlidePanel';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import { CONCIERGE_VISIBLE, MESSAGES_VISIBLE } from '../../../config/featureFlags';
import { useClientChatSummary } from '../../messages/embed/clientChatSummary';
import { canTextClients } from '../../messages/data/roles';
import { getAccess, isSelfEmployedMe } from '../../permissions/access';
import { getUsageState } from '../../pricing/usageStore';

import { useReception } from '../../../_utils/receptionMode';
import './clientMessages.css';
import { isOwnerHere } from '../../permissions/access';
import { formatUnitPence } from '../../pricing/money';

// The chat (and the Messages styles) load only when someone opens it
const ClientChatEmbed = React.lazy(() => import('../../messages/embed/ClientChatEmbed'));

/**
 * Client profile › Messages (Charlie, 2026-10-01): everything this client has
 * sent and been sent, as conversations, newest first.
 *
 *   Direct     - texts and emails someone here wrote, and the client's replies
 *   Bailey     - their booking-page chats with the receptionist (step in)
 *   Marketing  - campaigns and journeys
 *   Reminders  - confirmations, reminders and other automated texts
 *
 * Consent (what they agreed to) is the strip at the top, with its history a
 * tap away. Marketing, journeys and consent are loaded by the panel and passed
 * in; direct messages and reminders come from /client-messages.
 */
const FILTERS = [['all', 'All'], ['direct', 'Direct'], ['bailey', 'Bailey'], ['marketing', 'Marketing'], ['reminder', 'Reminders']];
// With Beu Messages (one client inbox, 13 Oct 2026): texts, Bailey and
// reminders all live in the client's chat. What came before Messages stays
// under Earlier until the server moves it into the chat (backend brief §27).
const CHAT_FILTERS = [['all', 'All'], ['chat', 'Chat'], ['marketing', 'Marketing'], ['earlier', 'Earlier']];
const BEFORE_MESSAGES = { direct: true, bailey: true, reminder: true };
const ICON = {
    direct: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />,
    marketing: <><path d="M3 11l15-6v14L3 13z" /><path d="M7 13v5a2 2 0 0 0 4 0v-4" /></>,
    journey: <><circle cx="5" cy="6" r="2" /><circle cx="19" cy="18" r="2" /><path d="M7 6h6a4 4 0 0 1 0 8H11a4 4 0 0 0 0 8" /></>,
    reminder: <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5M5 3L2 6M19 3l3 3" /></>,
};
const Glyph = ({ kind }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICON[kind] || ICON.direct}</svg>
);
const BaileyFace = () => (
    <svg width="22" height="22" viewBox="0 0 46 46" aria-hidden="true"><ellipse cx="17" cy="22" rx="5" ry="6" fill="#fff" /><ellipse cx="29" cy="22" rx="5" ry="6" fill="#fff" /><circle cx="17.6" cy="22.8" r="2.6" fill="#29282D" /><circle cx="29.6" cy="22.8" r="2.6" fill="#29282D" /><path d="M19 32c2.4 1.8 5.6 1.8 8 0" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>
);

const ts = (d) => (d ? new Date(d).getTime() : 0);
function shortWhen(d) {
    if (!d) return '';
    const t = new Date(d); const now = new Date();
    if (t.toDateString() === now.toDateString()) return t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const days = (now - t) / 86400000;
    if (days < 6) return t.toLocaleDateString('en-GB', { weekday: 'short' });
    return t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
const dayLabel = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const timeOf = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const MKT_STATUS = { opened: 'Opened', clicked: 'Opened · clicked', delivered: 'Delivered', bounced: 'Didn’t arrive', failed: 'Didn’t send', pending: 'Sending' };

export default function ClientMessagesTab({ clientId, clientName, marketing, journeys, consent, initialFilter = 'all', openChatAt = null }) {
    const history = useHistory();
    const first = (clientName || '').split(' ')[0] || 'This client';
    // Old links (the bell's ?tab=bailey) land on Earlier, where those threads now sit
    const [filter, setFilter] = useState(MESSAGES_VISIBLE && BEFORE_MESSAGES[initialFilter] ? 'earlier' : initialFilter);
    const chat = useClientChatSummary(clientId, MESSAGES_VISIBLE);
    // The header's Message button: straight into the chat
    useEffect(() => { if (openChatAt && MESSAGES_VISIBLE) setOpen('chat'); }, [openChatAt]);
    const [data, setData] = useState(null);       // { messages, compose }
    const [error, setError] = useState('');
    const [open, setOpen] = useState(null);       // a thread key
    const [consentOpen, setConsentOpen] = useState(false);
    // A locked front desk without Bailey: no Bailey filter or Bailey chats.
    const { baileyOff } = useReception();
    const showBailey = CONCIERGE_VISIBLE && !baileyOff;
    const inbox = useInbox({ clientUserId: clientId, enabled: showBailey && !!clientId });
    const baileyId = open && open.startsWith('bailey:') ? open.slice(7) : null;
    const convo = useConversation(baileyId, { onChange: inbox.patchRow });
    const [assistant, setAssistant] = useState({ name: 'Bailey', look: null });

    const load = useCallback(() => {
        api.load(clientId).then(setData).catch((e) => { setData({ messages: [], compose: null }); setError(e.message); });
    }, [clientId]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!CONCIERGE_VISIBLE) return;
        conciergeService.getSettings().then((r) => { const s = unwrap(r).settings; if (s) setAssistant({ name: s.assistantName || 'Bailey', look: s.avatar || null }); }).catch(() => {});
    }, []);

    // Every conversation, one shape: { key, kind, title, sub, preview, at, unread }
    const threads = useMemo(() => {
        const out = [];
        const msgs = (data && data.messages) || [];
        const direct = msgs.filter((m) => m.kind === 'direct');
        if (direct.length) {
            const last = direct[direct.length - 1];
            out.push({
                key: 'direct', kind: 'direct', title: `You and ${first}`, sub: 'Direct',
                preview: `${last.direction === 'in' ? `${first}: ` : 'You: '}${last.subject ? `${last.subject} · ` : ''}${last.text}`,
                at: last.at, unread: direct.filter((m) => m.unread).length,
            });
        }
        // Automated texts, one conversation per booking.
        const groups = new Map();
        msgs.filter((m) => m.kind !== 'direct').forEach((m) => {
            const k = m.bookingId ? `booking:${m.bookingId}` : `label:${m.label}`;
            if (!groups.has(k)) groups.set(k, []);
            groups.get(k).push(m);
        });
        groups.forEach((list, k) => {
            const last = list[list.length - 1];
            out.push({
                key: `rem:${k}`, kind: 'reminder', title: list.length > 1 ? list.map((x) => x.label).filter((v, i, a) => a.indexOf(v) === i).join(' · ') : last.label,
                sub: 'Reminders and confirmations', preview: last.text, at: last.at, items: list,
                status: `${list.length} ${list[0].channel === 'email' ? 'email' : 'text'}${list.length === 1 ? '' : 's'}${list.some((x) => x.status === 'failed') ? ' · one didn’t send' : ''}`,
            });
        });
        (showBailey ? (inbox.rows || []) : []).forEach((r) => out.push({
            key: `bailey:${r._id}`, kind: 'bailey',
            title: r.needsReply ? 'Waiting for you' : r.takeover ? 'With you' : r.booked ? `Booked with ${assistant.name}` : `Chat with ${assistant.name}`,
            sub: `${assistant.name} · booking page`, preview: r.preview || '', at: r.lastActivityAt, unread: r.needsReply ? 1 : 0,
        }));
        ((marketing && marketing.items) || []).forEach((m) => out.push({
            key: `mkt:${m.id}`, kind: 'marketing', title: m.name || 'Campaign', sub: `Marketing · ${m.channel === 'sms' ? 'text' : 'email'}`,
            preview: m.subject || m.name || '', at: m.sentAt, status: MKT_STATUS[m.status] || '', item: m,
        }));
        ((journeys && journeys.runs) || []).forEach((r) => out.push({
            key: `jrn:${r.runId}`, kind: 'journey', title: r.journeyName || 'Journey', sub: 'Marketing · journey',
            preview: r.stepTotal ? `Step ${r.stepPosition} of ${r.stepTotal}` : 'No steps yet',
            at: r.lastSentAt || r.updatedAt || r.startedAt || r.wakeAt, status: r.status === 'active' && r.wakeAt ? `Next ${shortWhen(r.wakeAt)}` : r.status, run: r,
        }));
        if (MESSAGES_VISIBLE) {
            // One chat with the client, first; the old threads become Earlier
            out.forEach((t) => { if (BEFORE_MESSAGES[t.kind]) { t.earlier = true; } });
            const bailey = chat && chat.bailey;
            out.push({
                key: 'chat', kind: 'chat', title: `Chat with ${first}`,
                sub: bailey === 'needs-you' ? 'Needs you' : bailey === 'replying' ? 'Bailey is replying' : 'Messages',
                preview: (chat && chat.preview) || `Message ${first} by text, email or the Beu app`,
                at: chat && chat.at, unread: bailey === 'needs-you' ? 1 : 0, pinnedTop: true,
            });
        }
        return out.sort((a, b) => (b.pinnedTop ? 1 : 0) - (a.pinnedTop ? 1 : 0) || ts(b.at) - ts(a.at));
    }, [data, inbox.rows, marketing, journeys, first, assistant.name, showBailey, chat]);

    const matchesFilter = (t, k) => {
        if (MESSAGES_VISIBLE) {
            if (k === 'earlier') return !!t.earlier;
            if (k === 'all') return !t.earlier;
            return k === 'chat' ? t.kind === 'chat' : t.kind === k || (k === 'marketing' && t.kind === 'journey');
        }
        return k === 'all' || t.kind === k || (k === 'marketing' && t.kind === 'journey');
    };
    const inFilter = (t) => matchesFilter(t, filter);
    const filterList = MESSAGES_VISIBLE ? CHAT_FILTERS : FILTERS;
    const counts = filterList.reduce((acc, [k]) => ({ ...acc, [k]: threads.filter((t) => matchesFilter(t, k)).length }), {});
    const shown = threads.filter(inFilter);
    const current = open ? threads.find((t) => t.key === open) || (open === 'direct' ? { key: 'direct', kind: 'direct', title: `You and ${first}`, sub: 'Direct' } : null) : null;
    // New message and "Message Grace directly" go to the chat once Messages is on
    const newMessage = MESSAGES_VISIBLE ? 'chat' : 'direct';

    const openThread = (t) => {
        hapticTick();
        setOpen(t.key);
        if (t.kind === 'direct' && t.unread) api.markRead(clientId).then(load).catch(() => {});
    };

    // The chat brings its own bar (Messages back, assign, Mark done): one header, not two
    if (current && current.kind === 'chat') {
        return (
            <div className="cm">
                <React.Suspense fallback={<div className="cm-skel" />}><ClientChatEmbed clientId={clientId} clientName={clientName} onBack={() => setOpen(null)} /></React.Suspense>
            </div>
        );
    }

    if (current) {
        return (
            <div className="cm">
                <div className="cm-thread-head">
                    <button type="button" className="cm-back" onClick={() => setOpen(null)} aria-label="Back to messages">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
                        Messages
                    </button>
                    <div className="cm-thread-title"><b>{current.title}</b><small>{current.sub}</small></div>
                </div>
                {current.kind === 'direct' && <DirectThread clientId={clientId} first={first} data={data} onSent={load} history={history} />}
                {current.kind === 'bailey' && (
                    <div className="cm-bailey">
                        <ReceptionThread compact convo={convo.convo} error={convo.error} sending={convo.sending} onReply={convo.reply} onTakeover={convo.takeover} assistantName={assistant.name} look={assistant.look} />
                    </div>
                )}
                {current.kind === 'reminder' && (
                    <div className="cm-cards">
                        {current.items.map((m) => (
                            <div className="cm-card" key={m.id}>
                                <div className="cm-card-top"><span className="cm-kind">{m.label} · {m.channel === 'email' ? 'email' : 'text'}</span><small>{shortWhen(m.at)} {timeOf(m.at)}</small></div>
                                {m.subject && <b>{m.subject}</b>}
                                <p>{m.text}</p>
                                <span className={`cm-chip${m.status === 'failed' ? ' is-bad' : ''}`}>{m.status === 'failed' ? 'Didn’t send' : 'Sent'}</span>
                            </div>
                        ))}
                        <button type="button" className="cm-ghost" onClick={() => setOpen(newMessage)}>Message {first} directly</button>
                    </div>
                )}
                {current.kind === 'marketing' && (
                    <div className="cm-cards">
                        <div className="cm-card">
                            <div className="cm-card-top"><span className="cm-kind">Campaign · {current.item.channel === 'sms' ? 'text' : 'email'}</span><small>{shortWhen(current.item.sentAt)}</small></div>
                            <b>{current.item.subject || current.item.name}</b>
                            {current.item.subject && current.item.name !== current.item.subject && <p>{current.item.name}</p>}
                            {current.status && <span className={`cm-chip${/send|arrive/.test(current.status) ? ' is-bad' : ''}`}>{current.status}</span>}
                        </div>
                        <button type="button" className="cm-ghost" onClick={() => setOpen(newMessage)}>Message {first} directly</button>
                    </div>
                )}
                {current.kind === 'journey' && (
                    <div className="cm-cards">
                        <div className="cm-card">
                            <div className="cm-card-top"><span className="cm-kind">Journey</span><span className="cm-chip">{current.run.status}</span></div>
                            <b>{current.run.journeyName}</b>
                            <p>
                                {current.run.stepTotal ? `Step ${current.run.stepPosition} of ${current.run.stepTotal}` : 'No steps yet'}
                                {current.run.messagesSent ? ` · ${current.run.messagesSent} message${current.run.messagesSent === 1 ? '' : 's'} sent` : ' · nothing sent yet'}
                                {current.run.status === 'active' && current.run.wakeAt ? ` · next ${current.run.nextStepType || 'step'} ${shortWhen(current.run.wakeAt)}` : ''}
                            </p>
                            {current.run.heldReason && <p className="cm-muted">Waiting: {current.run.heldReason}</p>}
                        </div>
                        <button type="button" className="cm-ghost" onClick={() => setOpen(newMessage)}>Message {first} directly</button>
                    </div>
                )}
            </div>
        );
    }

    const c = consent && consent.current;
    const chip = (on, label) => <span className={`cm-consent-chip${on ? '' : ' is-off'}`}>{label} {on ? '✓' : '✕'}</span>;
    return (
        <div className="cm">
            <div className="cm-head">
                <div><b>Messages</b><small>Everything {first} has sent and been sent, newest first</small></div>
                <button type="button" className="cm-primary" onClick={() => { hapticTick(); setOpen(newMessage); }}>New message</button>
            </div>
            {c && (
                <div className="cm-consent">
                    <span className="cm-kind">{first} gets</span>
                    {chip(c.smsMarketingConsent !== false, 'Marketing texts')}
                    {chip(c.emailMarketingConsent !== false, 'Marketing emails')}
                    <button type="button" className="cm-link" onClick={() => setConsentOpen(true)}>History</button>
                </div>
            )}
            <div className="cm-filters" role="tablist" aria-label="Filter messages">
                {filterList.filter(([k]) => (k !== 'bailey' || showBailey) && (k !== 'earlier' || counts.earlier > 0)).map(([k, label]) => (
                    <button key={k} type="button" role="tab" aria-selected={filter === k} className={`cm-filter${filter === k ? ' is-on' : ''}`} onClick={() => { hapticTick(); setFilter(k); }}>
                        {label} <span>{counts[k]}</span>
                    </button>
                ))}
            </div>
            {error && <p className="cm-error" role="alert">{error}</p>}
            {data === null && <div className="cm-skel" />}
            {data !== null && !shown.length && (
                <div className="cm-empty">{filter === 'all' ? `No messages with ${first} yet.` : 'Nothing here yet.'} <button type="button" className="cm-link" onClick={() => setOpen(newMessage)}>Send {first} a message</button></div>
            )}
            <div className="cm-list">
                {shown.map((t) => (
                    <button key={t.key} type="button" className="cm-row" onClick={() => openThread(t)}>
                        <span className={`cm-tile is-${t.kind === 'chat' ? 'direct' : t.kind}`}>{t.kind === 'bailey' ? <BaileyFace /> : <Glyph kind={t.kind === 'chat' ? 'direct' : t.kind} />}</span>
                        <span className="cm-row-text">
                            <span className="cm-row-top"><span className="cm-kind">{t.sub}</span><small>{shortWhen(t.at)}</small></span>
                            <span className="cm-row-title"><b>{t.title}</b>{t.unread > 0 && <span className="cm-dot" aria-label="Unread" />}</span>
                            <span className="cm-row-preview">{t.preview}</span>
                            {t.status && <span className="cm-row-status">{t.status}</span>}
                        </span>
                    </button>
                ))}
                {marketing && marketing.hasMore && (filter === 'all' || filter === 'marketing') && (
                    <button type="button" className="cm-ghost" disabled={marketing.loadingMore} onClick={marketing.loadMore}>{marketing.loadingMore ? 'Loading…' : 'Show older marketing'}</button>
                )}
            </div>
            {consentOpen && consent && consent.render && (
                <SlidePanel title={`What ${first} agreed to`} onClose={() => setConsentOpen(false)} width={480} zIndex={100010} className="bly-panel">
                    <div className="cm-consent-panel">{consent.render()}</div>
                </SlidePanel>
            )}
        </div>
    );
}

/** The direct conversation: bubbles, then a Text / Email composer. */
function DirectThread({ clientId, first, data, onSent, history }) {
    const compose = (data && data.compose) || {};
    const msgs = ((data && data.messages) || []).filter((m) => m.kind === 'direct');
    const [ch, setCh] = useState(compose.canText !== false ? 'sms' : 'email');
    const [text, setText] = useState('');
    const [subject, setSubject] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // The server's price, else Billing's (the same 8p and 2p as every other text and email)
    const prices = ((getUsageState() || {}).usage || {}).prices || {};
    const textPence = compose.smsPence != null ? compose.smsPence : prices.textPence != null ? prices.textPence : 8;
    const emailPence = compose.emailPence != null ? compose.emailPence : prices.emailPence != null ? prices.emailPence : 2;
    const blocked = ch === 'sms' ? (!compose.canText && (compose.textsBlocked || `${first} can’t be texted.`)) : (!compose.canEmail && (compose.emailBlocked || `${first} can’t be emailed.`));
    const send = async () => {
        if (!text.trim() || busy || blocked) return;
        setBusy(true); setError('');
        try {
            await api.send(clientId, { channel: ch, text, subject: ch === 'email' ? subject : undefined });
            hapticSuccess();
            setText(''); setSubject('');
            onSent();
        } catch (e) { hapticError(); setError(e.message); }
        setBusy(false);
    };
    let lastDay = '';
    return (
        <>
            <div className="cm-bubbles">
                {!msgs.length && <p className="cm-muted cm-center">No direct messages with {first} yet. Say hello below.</p>}
                {msgs.map((m) => {
                    const d = dayLabel(m.at);
                    const sep = d !== lastDay ? <div className="cm-day" key={`d${m.id}`}>{d}</div> : null;
                    lastDay = d;
                    return (
                        <React.Fragment key={m.id}>
                            {sep}
                            <div className={`cm-bubble-wrap ${m.direction === 'in' ? 'is-in' : 'is-out'}`}>
                                {m.subject && <small className="cm-subject">Subject: {m.subject}</small>}
                                <div className={`cm-bubble${m.status === 'failed' ? ' is-failed' : ''}`}>{m.text}</div>
                                <small className="cm-meta">
                                    {m.direction === 'in' ? first : (m.byName || 'You')} · {m.channel === 'email' ? 'Email' : 'Text'} · {timeOf(m.at)}{m.status === 'failed' ? ' · didn’t send' : ''}
                                </small>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
            {compose.canSend === false || !canTextClients(getAccess(), isSelfEmployedMe()) ? (
                // Sending spends the salon's message credits: the server says
                // who may (clientMessageService.maySend). Older servers don't
                // send canSend, so the composer shows as before.
                <div className="cm-compose">
                    <p className="cm-blocked">{compose.sendBlocked || `You can read messages with ${first}, but your role can’t send them.`}</p>
                </div>
            ) : (
            <div className="cm-compose">
                <div className="cm-compose-top">
                    <div className="cm-seg" role="tablist" aria-label="Send as">
                        <button type="button" role="tab" aria-selected={ch === 'sms'} className={ch === 'sms' ? 'is-on' : ''} onClick={() => setCh('sms')}>Text</button>
                        <button type="button" role="tab" aria-selected={ch === 'email'} className={ch === 'email' ? 'is-on' : ''} onClick={() => setCh('email')}>Email</button>
                    </div>
                    {/* One price for a text wherever it's sent from, from the balance: there
                        is no separate allowance for these (texting plan, 14 Oct 2026) */}
                    <small className="cm-hint">
                        {ch === 'sms'
                            ? (compose.repliesCome
                                ? <>{formatUnitPence(textPence)} a text from your balance. {first}’s replies land here.</>
                                : <>{formatUnitPence(textPence)} a text from your balance. Sent from Beu’s number, so {first} can’t reply. {isOwnerHere() && <button type="button" className="cm-link" onClick={() => history.push('/settings/business-numbers')}>Get a number</button>}</>)
                            : <>{formatUnitPence(emailPence)} an email from your balance. {compose.emailRepliesCome ? `${first}’s replies land here.` : 'Replies go to your salon email.'}</>}
                    </small>
                </div>
                {blocked && <p className="cm-blocked">{blocked}</p>}
                {error && <p className="cm-error" role="alert">{error}</p>}
                {ch === 'email' && !blocked && (
                    <>
                        <label htmlFor="cm-subject" className="cm-sr">Subject</label>
                        <input id="cm-subject" className="cm-input" placeholder="Subject" maxLength={120} value={subject} onChange={(e) => setSubject(e.target.value)} />
                    </>
                )}
                <div className="cm-compose-row">
                    <label htmlFor="cm-text" className="cm-sr">Message</label>
                    <textarea id="cm-text" className="cm-input" rows={2} disabled={!!blocked} placeholder={ch === 'sms' ? `Text ${first}…` : `Email ${first}…`} value={text}
                        maxLength={ch === 'sms' ? 600 : 5000} onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }} />
                    <button type="button" className="cm-send" disabled={!text.trim() || busy || !!blocked} onClick={send} aria-label="Send">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </button>
                </div>
            </div>
            )}
        </>
    );
}
